import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs';
import { AuthService, Role } from '../services/auth.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { FeatureFlag } from '../shared/feature-flags';
import { SuperAdminAuthService } from '../super-admin/super-admin-auth.service';

/*
 * Route guards for the access levels. They only choose the right screen: the backend checks role,
 * customer status and ownership on every request.
 */

/** Login screens: a signed-in user goes straight to their own start page. */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);

  return auth.isLoggedIn() ? inject(Router).parseUrl(auth.homePath()) : true;
};

/** Member area: any customer who can log in (a customer that is not yet active gets the shielded dashboard). */
export const memberGuard: CanActivateFn = (_route, state) => requireRole('lid', state);

/** Admin area. */
export const adminGuard: CanActivateFn = (_route, state) => requireRole('beheerder', state);

/**
 * Not signed in: to the login, with the address that was asked for in `terug`, so the link in a mail ends on the
 * update after logging in. The login only follows it within the area of the role (returnPathAfterLogin()).
 */
function requireRole(role: Role, state: RouterStateSnapshot) {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isLoggedIn()) {
    return router.createUrlTree(['/inloggen'], { queryParams: { terug: state.url } });
  }

  return auth.currentUser()?.role === role ? true : router.parseUrl('/geen-toegang');
}

/** Superbeheer: its own key; without one, its own login. */
export const superAdminGuard: CanActivateFn = () =>
  inject(SuperAdminAuthService).isLoggedIn() ? true : inject(Router).parseUrl('/superadmin/inloggen');

/** The superbeheer login: already signed in there, straight to the audit log. */
export const superAdminGuestGuard: CanActivateFn = () =>
  inject(SuperAdminAuthService).isLoggedIn() ? inject(Router).parseUrl('/superadmin/overzicht') : true;

/** A screen of a feature that is off for the signed-in user is not there: the no-access page, once the flags are known. */
export function featureFlagGuard(flag: FeatureFlag): CanActivateFn {
  return () => {
    const featureFlags = inject(FeatureFlagService);
    const router = inject(Router);
    const isOn = featureFlags.isOn(flag);

    return featureFlags.whenKnown().pipe(map(() => (isOn() ? true : router.parseUrl('/geen-toegang'))));
  };
}

/**
 * Where the login goes after signing in: the `terug` address when it is a path in the area of the role (/leden/ for a
 * member, /beheer/ for an administrator), else the start page. Never another site: no "//host", no scheme, no
 * backslash that a browser would read as a slash.
 */
export function returnPathAfterLogin(returnPath: string | null, role: Role | null): string | null {
  const area = role === 'beheerder' ? '/beheer/' : role === 'lid' ? '/leden/' : null;

  if (area === null || returnPath === null || returnPath.includes('\\')) {
    return null;
  }

  return returnPath.startsWith(area) ? returnPath : null;
}
